import React, { useEffect } from "react";
import { X, AlertTriangle, Info, Trash2 } from "lucide-react";
import { Button } from "../../../components/ui/button";

/**
 * A professional, reusable alert/confirmation modal.
 * Intent: 'info' | 'warning' | 'danger'
 */
export default function AutoReplyAlertModal({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  intent = "info",
  icon: CustomIcon,
  dismissible = true,
  compact = false,
}) {
  useEffect(() => {
    if (!open || !dismissible) return undefined;

    const handleKeyDown = event => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [dismissible, onClose, open]);

  if (!open) return null;

  const intents = {
    info: {
      bg: "bg-emerald-50",
      iconBg: "bg-emerald-100",
      iconColor: "text-emerald-600",
      button: "bg-emerald-600 hover:bg-emerald-700",
      Icon: Info,
    },
    warning: {
      bg: "bg-amber-50",
      iconBg: "bg-amber-100",
      iconColor: "text-amber-600",
      button: "bg-amber-600 hover:bg-amber-700",
      Icon: AlertTriangle,
    },
    danger: {
      bg: "bg-rose-50",
      iconBg: "bg-rose-100",
      iconColor: "text-rose-600",
      button: "bg-rose-600 hover:bg-rose-700",
      Icon: Trash2,
    },
  };

  const config = intents[intent] || intents.info;
  const ActiveIcon = CustomIcon || config.Icon;
  const handleClose = () => {
    if (dismissible) {
      onClose?.();
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
      return;
    }

    onClose?.();
  };

  if (compact) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div
          className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
          onClick={handleClose}
        />
        <div
          role="dialog"
          aria-modal="true"
          aria-label={title}
          className="relative w-full max-w-[400px] overflow-hidden rounded-lg bg-white shadow-xl"
        >
          <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-3">
            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${config.iconBg} ${config.iconColor}`}>
              <ActiveIcon size={17} strokeWidth={2.25} />
            </div>
            <h3 className="min-w-0 flex-1 text-sm font-semibold text-slate-900">
              {title}
            </h3>
            {dismissible ? (
              <button
                type="button"
                aria-label="Close confirmation"
                title="Close"
                onClick={handleClose}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <X size={16} />
              </button>
            ) : null}
          </div>

          <div className="px-4 py-3 text-sm leading-6 text-slate-600">
            {message}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-4 py-3">
            {cancelLabel ? (
              <Button
                variant="outline"
                onClick={handleClose}
                className="h-9 border-slate-200 px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              >
                {cancelLabel}
              </Button>
            ) : null}
            <Button
              onClick={handleConfirm}
              className={`h-9 px-4 text-sm font-semibold text-white shadow-sm ${config.button}`}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px] transition-opacity animate-in fade-in duration-300" 
        onClick={handleClose}
      />
      
      {/* Modal Card */}
      <div className="relative w-full max-w-[440px] overflow-hidden rounded-2xl bg-white shadow-2xl animate-in zoom-in-95 fade-in duration-200">
        



        <div className="p-6">
          <div className="flex items-start gap-4">
            {/* Icon Circle */}
            <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${config.iconBg} ${config.iconColor} shadow-sm ring-4 ring-white`}>
              <ActiveIcon size={24} strokeWidth={2.25} />
            </div>

            <div className="flex-1 pt-1">
              <div className="flex items-start justify-between">
                <h3 className="text-lg font-bold tracking-tight text-slate-900">
                  {title}
                </h3>
                {dismissible ? (
                  <button
                    onClick={onClose}
                    className="rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                  >
                    <X size={18} />
                  </button>
                ) : null}
              </div>
              
              {typeof message === "string" ? (
                <p className="mt-2.5 text-sm leading-relaxed text-slate-600">
                  {message}
                </p>
              ) : (
                <div className="mt-2.5 text-sm leading-relaxed text-slate-600">
                  {message}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-8 flex items-center justify-end gap-3">
            {cancelLabel ? (
              <Button
                variant="outline"
                onClick={onClose}
                className="h-10 border-slate-200 px-5 text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              >
                {cancelLabel}
              </Button>
            ) : null}
            <Button
              onClick={handleConfirm}
              className={`h-10 px-6 text-sm font-semibold text-white shadow-md transition-all active:scale-95 ${config.button}`}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
